import React from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Stack,
  Card,
  CardContent,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Grid,
  Fade,
  Zoom,
} from "@mui/material";
import {
  CloudUpload,
  Preview,
  Edit,
  Send,
  CheckCircle,
  Lightbulb,
  TableChart,
  Email,
  Security,
  Speed,
} from "@mui/icons-material";
import { Link } from "react-router-dom";

const steps = [
  {
    label: "Prepare Your Excel File",
    description: "Create and format your Excel file with recipient data",
    icon: <TableChart color="primary" />,
    content: (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ color: "#1f2937", fontWeight: 600 }}>
          Excel File Requirements
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Your Excel file should contain the following columns (headers are case-insensitive):
          </Typography>
        </Alert>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%", border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  Name Column
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Column header: <strong>Name</strong><br/>
                  Contains: Recipient's full name<br/>
                  Example: "John Doe", "Jane Smith"
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%", border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  Email Column
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Column header: <strong>Email</strong><br/>
                  Contains: Valid email addresses<br/>
                  Example: "john@example.com"
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%", border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  Mobile Column
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Column headers: <strong>Mobile</strong>, <strong>MobileNo</strong>, or <strong>Mobile Number</strong><br/>
                  Contains: Phone numbers (optional)<br/>
                  Example: "+1234567890"
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom sx={{ color: "#1f2937", fontWeight: 600 }}>
          Excel File Format
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <CheckCircle color="success" />
            </ListItemIcon>
            <ListItemText 
              primary="Supported formats: .xlsx, .xls"
              secondary="Modern Excel formats are recommended"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircle color="success" />
            </ListItemIcon>
            <ListItemText 
              primary="First row should contain column headers"
              secondary="Headers are automatically detected"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircle color="success" />
            </ListItemIcon>
            <ListItemText 
              primary="Data should start from the second row"
              secondary="Empty rows are automatically skipped"
            />
          </ListItem>
        </List>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Important:</strong> Only valid email addresses will be processed. Invalid emails will be automatically filtered out.
          </Typography>
        </Alert>
      </Box>
    ),
  },
  {
    label: "Upload Excel File",
    description: "Upload your prepared Excel file to the system",
    icon: <CloudUpload color="primary" />,
    content: (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ color: "#1f2937", fontWeight: 600 }}>
          Upload Process
        </Typography>
        
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Card sx={{ border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Step 1: Navigate to Email Sender
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click on "Email Sender" in the navigation menu or go to /email-sender
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Step 2: Upload Your File
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Drag and drop your Excel file onto the upload area, or click to browse and select your file
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Step 3: Verify Upload
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The system will automatically process your file and show a preview of extracted data
              </Typography>
            </CardContent>
          </Card>
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Tip:</strong> Make sure your Excel file is properly formatted before uploading to avoid processing errors.
          </Typography>
        </Alert>
      </Box>
    ),
  },
  {
    label: "Preview & Edit Data",
    description: "Review and modify the extracted recipient data",
    icon: <Preview color="primary" />,
    content: (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ color: "#1f2937", fontWeight: 600 }}>
          Data Review & Editing
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%", border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  What You Can Do
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Edit color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Edit recipient names, emails, and mobile numbers" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Edit color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Add new recipients manually" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Edit color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Remove unwanted recipients" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Edit color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Verify all email addresses are correct" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%", border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  Data Validation
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Only valid email addresses are processed" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Invalid emails are automatically filtered out" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText primary="You can see the total count of valid recipients" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Real-time validation as you edit" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Important:</strong> Take time to review your data before proceeding. Incorrect emails will not be delivered.
          </Typography>
        </Alert>
      </Box>
    ),
  },
  {
    label: "Compose Email",
    description: "Create your email content with rich formatting",
    icon: <Edit color="primary" />,
    content: (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ color: "#1f2937", fontWeight: 600 }}>
          Email Composition Features
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%", border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  Basic Requirements
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Email color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Subject line (required)" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Email color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Email message content (required)" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Email color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Personalization placeholders" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%", border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  Rich Text Editor
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Edit color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Bold, italic, underline formatting" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Edit color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Text alignment and colors" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Edit color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Insert images and videos" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Edit color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Professional email templates" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom sx={{ color: "#1f2937", fontWeight: 600 }}>
          Personalization
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Use these placeholders to personalize your emails:
          </Typography>
        </Alert>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Chip 
              label="[Customer Name]" 
              color="primary" 
              variant="outlined"
              sx={{ m: 0.5 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Will be replaced with each recipient's name
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Chip 
              label="[Mobile Number]" 
              color="primary" 
              variant="outlined"
              sx={{ m: 0.5 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Will be replaced with each recipient's mobile number
            </Typography>
          </Grid>
        </Grid>

        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Tip:</strong> Use the preview feature to see how your email will look before sending.
          </Typography>
        </Alert>
      </Box>
    ),
  },
  {
    label: "Send & Monitor",
    description: "Send your emails and track the results",
    icon: <Send color="primary" />,
    content: (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ color: "#1f2937", fontWeight: 600 }}>
          Sending Process
        </Typography>
        
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Card sx={{ border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Step 1: Review & Confirm
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Double-check your email content, subject, and recipient list before sending
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Step 2: Send Emails
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click the "Send" button to start the email delivery process
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Step 3: Monitor Progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Watch the progress bar and see real-time status of email delivery
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Step 4: View Results
              </Typography>
              <Typography variant="body2" color="text.secondary">
                See detailed results including successful sends and any failures
              </Typography>
            </CardContent>
          </Card>
        </Stack>

        <Typography variant="h6" gutterBottom sx={{ color: "#1f2937", fontWeight: 600 }}>
          Results & Monitoring
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%", border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
              <CardContent>
                <Typography variant="h6" color="success.main" gutterBottom>
                  Successful Sends
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Number of emails successfully delivered to recipients
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%", border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
              <CardContent>
                <Typography variant="h6" color="error.main" gutterBottom>
                  Failed Sends
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Number of emails that failed to deliver (with reasons)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%", border: "1px solid #e5e7eb", "&:hover": { boxShadow: 3 } }}>
              <CardContent>
                <Typography variant="h6" color="info.main" gutterBottom>
                  Total Processed
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total number of recipients processed in the campaign
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Email delivery may take a few minutes depending on the number of recipients and server load.
          </Typography>
        </Alert>
      </Box>
    ),
  },
];

const features = [
  {
    title: "Bulk Email Sending",
    description: "Send personalized emails to hundreds of recipients simultaneously",
    icon: <Speed color="primary" />,
  },
  {
    title: "Excel Integration",
    description: "Import recipient data directly from Excel files with automatic validation",
    icon: <TableChart color="primary" />,
  },
  {
    title: "Rich Text Editor",
    description: "Create professional emails with formatting, images, and videos",
    icon: <Edit color="primary" />,
  },
  {
    title: "Personalization",
    description: "Use placeholders to personalize emails for each recipient",
    icon: <Email color="primary" />,
  },
  {
    title: "Real-time Monitoring",
    description: "Track email delivery progress and view detailed results",
    icon: <CheckCircle color="primary" />,
  },
  {
    title: "Secure & Reliable",
    description: "Enterprise-grade security with reliable email delivery",
    icon: <Security color="primary" />,
  },
];

const tips = [
  "Always test your email with a small group before sending to all recipients",
  "Use clear, compelling subject lines to improve open rates",
  "Keep your email content concise and focused on your message",
  "Include a clear call-to-action in your emails",
  "Personalize your emails using the [Customer Name] placeholder",
  "Preview your email before sending to ensure proper formatting",
  "Monitor your email delivery results to improve future campaigns",
  "Use professional email templates for consistent branding",
];

export default function HowToUsePage() {
  const [activeStep, setActiveStep] = React.useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #e0e7ff 0%, #d1fae5 100%)",
        py: { xs: 2, md: 4 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Subtle Background Overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%)",
          zIndex: 0,
          opacity: 0.5,
        }}
      />

      <Container maxWidth="lg">
        {/* Header */}
        <Fade in timeout={1000}>
          <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: "#111827",
                fontFamily: "'Inter', sans-serif",
                mb: 2,
                fontSize: { xs: "1.75rem", md: "2.5rem" },
              }}
            >
              How to Use Email Sender Pro
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{
                maxWidth: 800,
                mx: "auto",
                fontFamily: "'Inter', sans-serif",
                lineHeight: 1.7,
                fontSize: { xs: "0.95rem", md: "1.125rem" },
              }}
            >
              A comprehensive guide to mastering our advanced bulk email solution. Follow these steps to send personalized, professional emails effortlessly.
            </Typography>
          </Box>
        </Fade>

        {/* Quick Start Button */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Button
            component={Link}
            to="/email-sender"
            variant="contained"
            size="large"
            startIcon={<CloudUpload />}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: { xs: "1rem", md: "1.125rem" },
              fontWeight: 600,
              fontFamily: "'Inter', sans-serif",
              textTransform: "none",
              borderRadius: 2,
              background: "linear-gradient(135deg, #667eea, #764ba2)",
              "&:hover": {
                background: "linear-gradient(135deg, #5a6fd8, #6a4190)",
                transform: "translateY(-2px)",
                boxShadow: 4,
              },
              transition: "all 0.3s ease",
            }}
          >
            Start Sending Emails Now
          </Button>
        </Box>

        {/* Features Grid */}
        <Box sx={{ mb: { xs: 4, md: 8 } }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: "#1f2937",
              fontFamily: "'Inter', sans-serif",
              textAlign: "center",
              mb: { xs: 3, md: 4 },
              fontSize: { xs: "1.5rem", md: "2rem" },
            }}
          >
            Key Features
          </Typography>
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Zoom in timeout={index * 300 + 600}>
                  <Card
                    sx={{
                      height: "100%",
                      border: "1px solid rgba(102, 126, 234, 0.2)",
                      transition: "all 0.3s ease",
                      background: "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
                      "&:hover": {
                        transform: "translateY(-6px)",
                        boxShadow: "0 12px 30px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    <CardContent sx={{ textAlign: "center", p: { xs: 2, md: 3 } }}>
                      <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          color: "#1f2937",
                          fontFamily: "'Inter', sans-serif",
                          fontSize: { xs: "1rem", md: "1.25rem" },
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "'Inter', sans-serif", fontSize: { xs: "0.9rem", md: "1rem" } }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Step-by-Step Guide */}
        <Fade in timeout={1000}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              background: "#ffffff",
              border: "1px solid rgba(102, 126, 234, 0.2)",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: "#1f2937",
                fontFamily: "'Inter', sans-serif",
                textAlign: "center",
                mb: { xs: 3, md: 4 },
                fontSize: { xs: "1.5rem", md: "2rem" },
              }}
            >
              Step-by-Step Guide
            </Typography>

            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={() => step.icon}
                    sx={{
                      "& .MuiStepLabel-label": {
                        fontWeight: 600,
                        fontSize: { xs: "1rem", md: "1.125rem" },
                        fontFamily: "'Inter', sans-serif",
                        color: "#1f2937",
                      },
                    }}
                  >
                    {step.label}
                  </StepLabel>
                  <StepContent>
                    <Typography
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        fontFamily: "'Inter', sans-serif",
                        fontSize: { xs: "0.95rem", md: "1rem" },
                      }}
                    >
                      {step.description}
                    </Typography>
                    {step.content}
                    <Box sx={{ mb: 2 }}>
                      <div>
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          sx={{
                            mt: 1,
                            mr: 1,
                            fontFamily: "'Inter', sans-serif",
                            textTransform: "none",
                            background: "linear-gradient(135deg, #667eea, #764ba2)",
                            "&:hover": {
                              background: "linear-gradient(135deg, #5a6fd8, #6a4190)",
                              transform: "translateY(-1px)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          {index === steps.length - 1 ? "Finish" : "Continue"}
                        </Button>
                        <Button
                          disabled={index === 0}
                          onClick={handleBack}
                          sx={{
                            mt: 1,
                            mr: 1,
                            fontFamily: "'Inter', sans-serif",
                            textTransform: "none",
                            color: "#667eea",
                            "&:hover": {
                              backgroundColor: "rgba(102, 126, 234, 0.1)",
                            },
                          }}
                        >
                          Back
                        </Button>
                      </div>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
            {activeStep === steps.length && (
              <Paper square elevation={0} sx={{ p: 3, m: 1, bgcolor: "rgba(102, 126, 234, 0.05)" }}>
                <Typography sx={{ mt: 1, mb: 1, fontFamily: "'Inter', sans-serif", fontSize: "1.125rem" }}>
                  All steps completed - you're ready to send emails!
                </Typography>
                <Button
                  onClick={handleReset}
                  sx={{
                    mt: 1,
                    fontFamily: "'Inter', sans-serif",
                    textTransform: "none",
                    color: "#667eea",
                    "&:hover": {
                      backgroundColor: "rgba(102, 126, 234, 0.1)",
                    },
                  }}
                >
                  Reset
                </Button>
              </Paper>
            )}
          </Paper>
        </Fade>

        {/* Tips Section */}
        <Box sx={{ mt: { xs: 4, md: 8 } }}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: "#1f2937",
              fontFamily: "'Inter', sans-serif",
              textAlign: "center",
              mb: { xs: 3, md: 4 },
              fontSize: { xs: "1.5rem", md: "2rem" },
            }}
          >
            Pro Tips for Better Results
          </Typography>
          <Grid container spacing={3}>
            {tips.map((tip, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Zoom in timeout={index * 300 + 600}>
                  <Card
                    sx={{
                      border: "1px solid rgba(102, 126, 234, 0.2)",
                      transition: "all 0.3s ease",
                      background: "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                        <Lightbulb color="primary" sx={{ mt: 0.5, fontSize: { xs: 24, md: 28 } }} />
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: "'Inter', sans-serif",
                            lineHeight: 1.6,
                            fontSize: { xs: "0.95rem", md: "1rem" },
                          }}
                        >
                          {tip}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Final CTA */}
        <Fade in timeout={1000}>
          <Box sx={{ textAlign: "center", mt: { xs: 4, md: 8 }, p: 4, bgcolor: "rgba(102, 126, 234, 0.05)", borderRadius: 3 }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: "#1f2937",
                fontFamily: "'Inter', sans-serif",
                mb: 2,
                fontSize: { xs: "1.25rem", md: "1.75rem" },
              }}
            >
              Ready to Start Sending?
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mb: 3,
                fontFamily: "'Inter', sans-serif",
                maxWidth: 600,
                mx: "auto",
                fontSize: { xs: "0.95rem", md: "1.125rem" },
              }}
            >
              Follow the steps above and launch your professional email campaigns today with Email Sender Pro.
            </Typography>
            <Button
              component={Link}
              to="/email-sender"
              variant="contained"
              size="large"
              startIcon={<Send />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: { xs: "1rem", md: "1.125rem" },
                fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
                textTransform: "none",
                borderRadius: 2,
                background: "linear-gradient(135deg, #667eea, #764ba2)",
                "&:hover": {
                  background: "linear-gradient(135deg, #5a6fd8, #6a4190)",
                  transform: "translateY(-2px)",
                  boxShadow: 4,
                },
                transition: "all 0.3s ease",
              }}
            >
              Get Started Now
            </Button>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
}